import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import axios from 'axios';
import { 
  FaHome, 
  FaCalendarCheck, 
  FaUtensils, 
  FaQrcode, 
  FaClipboardList, 
} from 'react-icons/fa';
import plogo from '../assets/plogo.png';

const Sidebar = ({ isOpen, toggleSidebar }) => {
  const location = useLocation();
  const [pendingOrderCount, setPendingOrderCount] = useState(0);
  const [newReservationCount, setNewReservationCount] = useState(0);
  
  // Refs to track previous counts to trigger sound only on INCREASES
  const prevOrderCountRef = useRef(0);
  const prevResCountRef = useRef(0);
  
  // Audio reference
  const notificationSound = useRef(new Audio('/sounds/notification.mp3'));

  const fetchData = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];

      // 1. Fetch Live Orders
      const orderResponse = await axios.get('https://admin.theporch.in/api/orders/live');
      let currentOrderCount = 0;
      if (orderResponse.data && Array.isArray(orderResponse.data)) {
        const pendingTodayOrders = orderResponse.data.filter(order => {
          const orderDate = order.created_at ? order.created_at.split('T')[0] : '';
          return order.status === 'Pending' && orderDate === today;
        });
        currentOrderCount = pendingTodayOrders.length;
      }

      // 2. Fetch Reservations
      const resResponse = await axios.get('https://admin.theporch.in/api/cafe_reservations');
      let currentResCount = 0;
      if (resResponse.data && Array.isArray(resResponse.data)) {
        const newReservations = resResponse.data.filter(res => res.status === 'New');
        currentResCount = newReservations.length;
      }

      // --- ONLY PLAY SOUND ONCE ON INCREASE ---
      // Check if current total is higher than the last known total
      const hasNewOrder = currentOrderCount > prevOrderCountRef.current;
      const hasNewReservation = currentResCount > prevResCountRef.current;

      if (hasNewOrder || hasNewReservation) {
        notificationSound.current.play().catch(() => {
          console.log("Interaction required for audio. Click anywhere on the page.");
        });
      }

      // Update refs so the next 10s check knows the current count
      prevOrderCountRef.current = currentOrderCount;
      prevResCountRef.current = currentResCount;

      // Update UI state
      setPendingOrderCount(currentOrderCount);
      setNewReservationCount(currentResCount);

    } catch (err) {
      console.error("Sidebar Sync Error:", err.message);
    }
  };

  useEffect(() => {
    fetchData(); 
    const interval = setInterval(fetchData, 10000); 
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (isOpen && window.innerWidth <= 1024) {
      toggleSidebar();
    }
  }, [location.pathname]);

  return (
    <>
      <style>{`
        .sidebar {
          width: 260px;
          background: #ffffff;
          height: 100vh;
          position: fixed;
          left: 0;
          top: 0;
          z-index: 1000;
          box-shadow: 2px 0 8px rgba(0,0,0,0.05);
          transition: transform 0.3s ease;
          overflow-y: auto;
        }
        .logo-container { text-align: center; padding: 30px 0; }
        .logo-container img { width: 100px; }
        .menu-section-label {
          padding: 10px 24px;
          font-size: 0.75rem;
          text-transform: uppercase;
          letter-spacing: 1px;
          color: #9ca3af;
          font-weight: 700;
          margin-top: 15px;
        }
        .nav-link {
          display: flex;
          align-items: center;
          padding: 12px 24px;
          text-decoration: none;
          color: #4b5563;
          margin: 4px 16px;
          border-radius: 10px;
          transition: 0.2s;
          font-size: 0.95rem;
          position: relative;
        }
        .nav-link.active {
          background: #f5f3f0;
          color: #AE7D54;
          font-weight: 600;
        }
        .nav-link .icon { margin-right: 12px; font-size: 1.1rem; }
        .badge {
          color: white;
          font-size: 0.7rem;
          font-weight: 800;
          padding: 2px 8px;
          border-radius: 20px;
          margin-left: auto;
          animation: shake 0.5s ease-in-out;
        }
        .badge-pending { background: #ef4444; box-shadow: 0 2px 4px rgba(239, 68, 68, 0.3); }
        .badge-new { background: #3b82f6; box-shadow: 0 2px 4px rgba(59, 130, 246, 0.3); }
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-2px); }
          75% { transform: translateX(2px); }
        }
        .overlay { display: none; }
        @media (max-width: 1024px) {
          .sidebar {
            top: 72px; 
            height: calc(100vh - 72px);
            transform: translateX(${isOpen ? '0' : '-100%'});
            z-index: 998;
          }
          .overlay {
            display: ${isOpen ? 'block' : 'none'};
            position: fixed;
            top: 72px; left: 0; right: 0; bottom: 0;
            background: rgba(0,0,0,0.4);
            z-index: 997;
          }
        }
      `}</style>

      <div className="overlay" onClick={toggleSidebar}></div>

      <div className="sidebar">
        <div className="logo-container">
          <img src={plogo} alt="Logo" />
        </div>

        <nav>
          <div className="menu-section-label">General</div>
          <NavLink to="/dashboard" className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}>
            <span className="icon"><FaHome /></span> Dashboard
          </NavLink>

          <NavLink to="/porch-reservations" className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}>
            <span className="icon"><FaCalendarCheck /></span> 
            <span style={{ flex: 1 }}>Reservations</span>
            {newReservationCount > 0 && (
              <span className="badge badge-new">{newReservationCount}</span>
            )}
          </NavLink>

          <NavLink to="/food" className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}>
            <span className="icon"><FaUtensils /></span> Menus
          </NavLink>

          <div className="menu-section-label">Table Scanner System</div>
          <NavLink to="/tables/live-orders" className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}>
            <span className="icon"><FaClipboardList /></span> 
            <span style={{ flex: 1 }}>Orders</span>
            {pendingOrderCount > 0 && (
              <span className="badge badge-pending">{pendingOrderCount}</span>
            )}
          </NavLink>

          <NavLink to="/tables/qr-codes" className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}>
            <span className="icon"><FaQrcode /></span> Table QR
          </NavLink>
        </nav>
      </div> 
    </>
  );
};

export default Sidebar;